import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar as MuiAvatar,
  Backdrop,
  Fade,
  Tooltip,
} from "@mui/material";
import {
  Phone,
  CallEnd,
  Mic,
  MicOff,
  VolumeUp,
  VolumeOff,
  Videocam,
  VideocamOff,
  ScreenShare,
  Close,
} from "@mui/icons-material";
import { useSocket } from "../../context/SocketContext";
import { useAppSelector } from "../../store/hooks";

const COLOR_MAP: Record<string, string> = {
  blue: "#3b82f6",
  teal: "#14b8a6",
  purple: "#8b5cf6",
  amber: "#f59e0b",
  rose: "#f43f5e",
  indigo: "#6366f1",
  green: "#22c55e",
};

function fmtTimer(s: number) {
  const m = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

// An animated overlay for incoming calls
const IncomingCall: React.FC = () => {
  const { socket } = useSocket();
  const user = useAppSelector((s) => s.auth.user);

  const [incomingCall, setIncomingCall] = useState<any>(null);
  const [callStatus, setCallStatus] = useState<"incoming" | "connected">(
    "incoming",
  );
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [speakerOff, setSpeakerOff] = useState(false);
  const [timer, setTimer] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const closeCall = () => {
    setIncomingCall(null);
    setCallStatus("incoming");
    setTimer(0);
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  useEffect(() => {
    if (callStatus === "connected" && incomingCall) {
      const isVideo = incomingCall.type === "video";
      navigator.mediaDevices
        .getUserMedia({ video: isVideo, audio: true })
        .then((stream) => {
          streamRef.current = stream;
          if (isVideo && localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
        })
        .catch(() => {});

      timerRef.current = setInterval(() => setTimer((s) => s + 1), 1000);
    }

    return () => {
      if (callStatus !== "connected") {
        if (timerRef.current) clearInterval(timerRef.current);
        streamRef.current?.getTracks().forEach((t) => t.stop());
      }
    };
  }, [callStatus, incomingCall]);

  useEffect(() => {
    if (!socket) return;

    socket.on("incoming-call", (data: any) => {
      setIncomingCall(data);
      setCallStatus("incoming");
    });

    socket.on("call-ended", () => {
      closeCall();
    });

    return () => {
      socket.off("incoming-call");
      socket.off("call-ended");
    };
  }, [socket]);

  const handleAccept = () => {
    if (!socket || !incomingCall) return;
    socket.emit("accept-call", {
      callerId: incomingCall.caller._id,
      receiverId: user?._id,
    });
    setCallStatus("connected");
  };

  const handleDecline = () => {
    if (!socket || !incomingCall) return;
    socket.emit("reject-call", {
      callerId: incomingCall.caller._id,
      receiverId: user?._id,
    });
    closeCall();
  };

  const handleEndCall = () => {
    if (socket && incomingCall) {
      socket.emit("end-call", { to: incomingCall.caller._id });
    }
    closeCall();
  };

  if (!incomingCall) return null;

  if (callStatus === "connected") {
    const isVideo = incomingCall.type === "video";
    const avatarColor = COLOR_MAP[incomingCall.caller.color] || "#3b82f6";

    if (!isVideo) {
      return (
        <Backdrop
          open
          sx={{
            zIndex: 2000,
            backdropFilter: "blur(12px)",
            background: "rgba(10,15,30,0.75)",
          }}
        >
          <Fade in>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                userSelect: "none",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 88,
                    height: 88,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}aa)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 30,
                    fontWeight: 700,
                    color: "#fff",
                    boxShadow: `0 0 40px ${avatarColor}66`,
                  }}
                >
                  {incomingCall.caller.initials}
                </Box>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  sx={{ fontSize: 26, fontWeight: 700, color: "#fff", mb: 0.5 }}
                >
                  {incomingCall.caller.name}
                </Typography>
                <Typography
                  sx={{ fontSize: 14, color: "#22c55e", fontWeight: 500 }}
                >
                  {fmtTimer(timer)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2.5, mt: 2 }}>
                <Tooltip title={speakerOff ? "Speaker On" : "Speaker Off"}>
                  <IconButton
                    onClick={() => setSpeakerOff((v) => !v)}
                    sx={{
                      width: 56,
                      height: 56,
                      background: speakerOff
                        ? "rgba(255,255,255,0.15)"
                        : "rgba(255,255,255,0.08)",
                      color: speakerOff ? "#f59e0b" : "#fff",

                    }}
                  >
                    {speakerOff ? <VolumeOff /> : <VolumeUp />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={muted ? "Unmute" : "Mute"}>
                  <IconButton
                    onClick={() => {
                      setMuted((m) => {
                        const next = !m;
                        streamRef.current?.getAudioTracks().forEach((t) => {
                          t.enabled = !next;
                        });
                        return next;
                      });
                    }}
                    sx={{
                      width: 56,
                      height: 56,
                      background: muted
                        ? "rgba(239,68,68,0.2)"
                        : "rgba(255,255,255,0.08)",
                      color: muted ? "#ef4444" : "#fff",

                    }}
                  >
                    {muted ? <MicOff /> : <Mic />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="End Call">
                  <IconButton
                    onClick={handleEndCall}
                    sx={{
                      width: 56,
                      height: 56,
                      background: "#ef4444",
                      color: "#fff",

                      transform: "rotate(135deg)",
                    }}
                  >
                    <Phone />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Fade>
        </Backdrop>
      );
    } else {
      return (
        <Backdrop open sx={{ zIndex: 2000, background: "rgba(5,8,20,0.92)" }}>
          <Fade in>
            <Box
              sx={{
                position: "relative",
                width: "min(900px, 95vw)",
                height: "min(580px, 90vh)",
                borderRadius: 4,
                overflow: "hidden",
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ textAlign: "center", opacity: 0.4 }}>
                  <Box
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: "50%",
                      mx: "auto",
                      mb: 2,
                      background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}88)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 32,
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    {incomingCall.caller.initials}
                  </Box>
                  <Typography
                    sx={{ color: "#fff", fontSize: 16, fontWeight: 600 }}
                  >
                    {incomingCall.caller.name}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  p: "12px 20px",
                  background:
                    "linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}
                  >
                    {incomingCall.caller.name}
                  </Typography>
                  <Typography sx={{ color: "#22c55e", fontSize: 12 }}>
                    {fmtTimer(timer)}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleEndCall}
                  sx={{ color: "#fff", background: "rgba(255,255,255,0.1)" }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 90,
                  right: 20,
                  width: 160,
                  height: 110,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "2px solid rgba(255,255,255,0.2)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                  background: cameraOff ? "#1e293b" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {cameraOff ? (
                  <VideocamOff sx={{ color: "#64748b", fontSize: 32 }} />
                ) : (
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transform: "scaleX(-1)",
                    }}
                  />
                )}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    left: 6,
                    fontSize: 10,
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 600,
                  }}
                >
                  You
                </Box>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: "16px 24px",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <Tooltip title={muted ? "Unmute" : "Mute"}>
                  <IconButton
                    onClick={() => {
                      setMuted((m) => {
                        const next = !m;
                        streamRef.current?.getAudioTracks().forEach((t) => {
                          t.enabled = !next;
                        });
                        return next;
                      });
                    }}
                    sx={{
                      width: 52,
                      height: 52,
                      background: muted
                        ? "rgba(239,68,68,0.25)"
                        : "rgba(255,255,255,0.12)",
                      color: muted ? "#ef4444" : "#fff",
                    }}
                  >
                    {muted ? <MicOff /> : <Mic />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="End Call">
                  <IconButton
                    onClick={handleEndCall}
                    sx={{
                      width: 60,
                      height: 60,
                      background: "#ef4444",
                      color: "#fff",

                      transform: "rotate(135deg)",
                    }}
                  >
                    <Phone />
                  </IconButton>
                </Tooltip>
                <Tooltip title={cameraOff ? "Camera On" : "Camera Off"}>
                  <IconButton
                    onClick={() => {
                      setCameraOff((c) => {
                        const next = !c;
                        streamRef.current?.getVideoTracks().forEach((t) => {
                          t.enabled = !next;
                        });
                        return next;
                      });
                    }}
                    sx={{
                      width: 52,
                      height: 52,
                      background: cameraOff
                        ? "rgba(239,68,68,0.25)"
                        : "rgba(255,255,255,0.12)",
                      color: cameraOff ? "#ef4444" : "#fff",
                    }}
                  >
                    {cameraOff ? <VideocamOff /> : <Videocam />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share Screen">
                  <IconButton
                    sx={{
                      width: 52,
                      height: 52,
                      background: "rgba(255,255,255,0.12)",
                      color: "#fff",
                    }}
                  >
                    <ScreenShare />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Fade>
        </Backdrop>
      );
    }
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(8px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: 320,
          background: "#fff",
          borderRadius: 4,
          p: 4,
          textAlign: "center",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          animation: "slideDown 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        }}
      >
        <style>
          {`
            @keyframes slideDown {
              0% { transform: translateY(-50px); opacity: 0; }
              100% { transform: translateY(0); opacity: 1; }
            }
            @keyframes ring {
              0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
              70% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(34, 197, 94, 0); }
              100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
            }
          `}
        </style>

        <Box sx={{ position: "relative", display: "inline-block", mb: 2 }}>
          <MuiAvatar
            sx={{
              width: 80,
              height: 80,
              background:
                COLOR_MAP[incomingCall.caller.color] ||
                incomingCall.caller.color ||
                "#3b82f6",
              fontSize: 28,
              mx: "auto",
            }}
          >
            {incomingCall.caller.initials}
          </MuiAvatar>
        </Box>

        <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 0.5 }}>
          {incomingCall.caller.name}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#64748b", mb: 4 }}>
          Incoming {incomingCall.type === "video" ? "Video" : "Voice"} Call...
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              onClick={handleDecline}
              sx={{
                width: 56,
                height: 56,
                background: "#ef4444",
                color: "#fff",

              }}
            >
              <CallEnd />
            </IconButton>
            <Typography
              sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}
            >
              Decline
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              onClick={handleAccept}
              sx={{
                width: 56,
                height: 56,
                background: "#22c55e",
                color: "#fff",
                animation: "ring 1.5s infinite ease-in-out",

              }}
            >
              <Phone />
            </IconButton>
            <Typography
              sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}
            >
              Accept
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default IncomingCall;
